<template>
  <div>
    <BrucdcumHeader :breadcrumb="['Inventory', 'Stock Adjustment']" />
    <div class="add-stock-adjustment-container">
      <form class="add-stock-adjustment-form">
      <div class="add-stock-adjustment-form-section add-stock-adjustment-card">
        <div class="add-stock-adjustment-form-row-flex">
          <div class="add-stock-adjustment-form-col add-stock-adjustment-form-group--label-dropdown">
            <LabelDropdown
              label="Warehouse"
              select-id="asa-warehouse"
              v-model="selectedWarehouse"
              flex
              wide
              required
              searchable
              menu-class="add-stock-adjustment-dropdown-menu"
              :options="warehouseDropdownOptions"
              @change="handleWarehouseChange"
            >
              <div class="add-stock-adjustment-input-helper-text">Choose the warehouse for this adjustment</div>
            </LabelDropdown>
          </div>
          <div class="add-stock-adjustment-form-col add-stock-adjustment-form-group--label-dropdown">
            <LabelDropdown
              label="Adjustment Account"
              select-id="asa-adjustment-account"
              v-model="selectedAdjustmentAccount"
              flex
              wide
              required
              searchable
              menu-class="add-stock-adjustment-dropdown-menu"
              :options="adjustmentAccountDropdownOptions"
              @change="handleAccountChange"
            >
              <div class="add-stock-adjustment-input-helper-text">Choose the account for this adjustment</div>
            </LabelDropdown>
          </div>
        </div>
        <div class="add-stock-adjustment-table-title">Stock Adjustment</div>
        <div class="add-stock-adjustment-table-responsive">
          <table class="add-stock-adjustment-transactions-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Comment</th>
              <th>Current Qty</th>
              <th>Adjusted Qty</th>
              <th>Current Value</th>
              <th>Sales/Purchase Rate</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in tableRows" :key="row.id">
              <td class="add-stock-adjustment-table-dropdown-cell">
                <LabelDropdown
                  hide-label
                  aria-label="Item"
                  :select-id="`asa-item-${row.id}`"
                  v-model="row.item"
                  flex
                  searchable
                  menu-class="add-stock-adjustment-dropdown-menu"
                  :options="itemDropdownOptions(row)"
                  @change="handleItemChange(row, idx)"
                />
              </td>
              <td class="add-stock-adjustment-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Comment"
                  :input-id="`asa-comment-${row.id}`"
                  v-model="row.comment"
                  flex
                />
              </td>
              <td class="add-stock-adjustment-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Current Qty"
                  :input-id="`asa-current-qty-${row.id}`"
                  :model-value="row.currentQty"
                  flex
                  readonly
                />
              </td>
              <td class="add-stock-adjustment-table-input-cell add-stock-adjustment-table-input-cell--with-message">
                <LabelInputField
                  hide-label
                  aria-label="Adjusted Qty"
                  :input-id="`asa-adjusted-qty-${row.id}`"
                  :model-value="row.adjustedQty"
                  type="number"
                  flex
                  :has-error="!!row.adjustedQtyError"
                  @update:modelValue="onAdjustedQtyChange(row, $event)"
                >
                  <div v-if="row.adjustedQtyError" class="add-stock-adjustment-adjusted-qty-error">
                    Adjusted Qty cannot reduce below zero or exceed current quantity!
                  </div>
                </LabelInputField>
              </td>
              <td class="add-stock-adjustment-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Current Value"
                  :input-id="`asa-current-value-${row.id}`"
                  v-model="row.currentValue"
                  flex
                />
              </td>
              <td class="add-stock-adjustment-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Sales/Purchase Rate"
                  :input-id="`asa-rate-${row.id}`"
                  v-model="row.rate"
                  flex
                />
              </td>
              <td><button class="add-stock-adjustment-btn add-stock-adjustment-btn-delete" type="button" @click="removeRow(idx)"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M7.5 8.5V14.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/><path d="M12.5 8.5V14.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/><rect x="4.5" y="5.5" width="11" height="11" rx="2" stroke="#fff" stroke-width="1.5"/><path d="M2.5 5.5H17.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/><path d="M8.5 2.5H11.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/></svg></button></td>
            </tr>
          </tbody>
          </table>
        </div>
        <div class="add-stock-adjustment-add-item-row">
          <PrimaryButton label="Add New Item" icon="bi bi-plus-lg" @click="addRow" />
        </div>
      </div>

      <div class="add-stock-adjustment-form-actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
        <PrimaryButton label="Save and New" icon="bi bi-arrow-repeat" @click="saveAndNew" />
        <PrimaryButton label="Save and Close" icon="bi bi-check-lg" @click="saveAndClose" />
      </div>
        <div v-if="showToast" :class="['add-stock-adjustment-custom-toast', toastType]">{{ toastMessage }}</div>
      </form>
    </div>
    
    <!-- Add New Warehouse Modal -->
    <AddNewWarehouse
      :visible="showWarehouseModal"
      :companyId="companyId"
      @ok="onAddWarehouseOk"
      @cancel="closeWarehouseModal"
    />
  </div>
</template>

<script>
import '../styles/AddNewStockAdjustment.css';
import '../styles/inventory-label-field-overrides.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import AddNewWarehouse from '@/modules/company/views/AddNewWarehouse.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'AddNewStockAdjustment',
  components: {
    BrucdcumHeader,
    AddNewWarehouse,
    LabelDropdown,
    LabelInputField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      tableRows: [
        { id: 1, item: '', comment: '', currentQty: '', adjustedQty: '', currentValue: '0.00', rate: '' }
      ],
      nextRowId: 2,
      selectedWarehouse: '',
      selectedAdjustmentAccount: '',
      warehouses: [],
      accounts: [],
      inventoryItems: [],
      saveMessage: '',
      saveMessageType: '', // 'success' or 'error'
      reorderPointErrorRows: [],
      adjustedItemIds: [],
      isSaving: false,
      showToast: false,
      toastMessage: '',
      toastType: '',
      showWarehouseModal: false,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
    warehouseDropdownOptions() {
      return [
        { value: '', label: 'Select Warehouse' },
        ...this.warehouses.map((warehouse) => ({
          value: warehouse.id,
          label: warehouse.name,
        })),
        { value: '__add_new__', label: 'Add New Warehouse' },
      ];
    },
    adjustmentAccountDropdownOptions() {
      return [
        { value: '', label: 'Select Adjustment Account' },
        { value: '__add_new__', label: 'Add a New Account' },
        ...this.accounts.map((account) => ({
          value: account.id,
          label: `${account.accountNumber || account.code} - ${account.accountName || account.name} - ${account.accountType || account.type}`,
        })),
      ];
    },
  },
  mounted() {
    this.fetchInventoryItems();
    this.fetchAdjustedItems();
    this.fetchAccounts();
    this.fetchWarehouses();
  },
  watch: {
    inventoryItems(newItems) {
      const itemId = this.$route.query.itemId;
      if (itemId && newItems.length && this.tableRows.length && !this.tableRows[0].item) {
        this.tableRows[0].item = Number(itemId);
      }
    }
  },
  methods: {
    async fetchInventoryItems() {
      const companyId = this.$route.params.id;
      const url = `${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch inventory items');
        const data = await response.json();
        this.inventoryItems = data;
      } catch (error) {
        // Optionally handle error
      }
    },
    async fetchAdjustedItems() {
      const companyId = this.$route.params.id;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/stock-adjustment-items`);
        if (!response.ok) throw new Error('Failed to fetch stock adjustment items');
        const items = await response.json();
        this.adjustedItemIds = items.map(item => item.inventoryItemId);
      } catch (e) {
        this.adjustedItemIds = [];
      }
    },
    async fetchAccounts() {
      const companyId = this.$route.params.id;
      try {
        const res = await ApiService.get(`/company-account/company/${companyId}`);
        this.accounts = res.data.accounts || [];
      } catch (error) {
        this.accounts = [];
      }
    },
    async fetchWarehouses() {
      const companyId = this.$route.params.id;
      try {
        const res = await ApiService.get(`/warehouses/company/${companyId}`);
        this.warehouses = res.data.data.warehouses || [];
      } catch (error) {
        logger.error('Error fetching warehouses:', error);
        // Fallback to mock data if API fails
        this.warehouses = [
          { id: 1, name: 'Main Warehouse' },
          { id: 2, name: 'Secondary Warehouse' },
          { id: 3, name: 'Storage Facility A' },
          { id: 4, name: 'Distribution Center' }
        ];
      }
    },
    itemDropdownOptions(row) {
      return [
        { value: '', label: 'Select Item' },
        ...this.getAvailableItems(row).map((item) => ({
          value: item.id,
          label: item.name,
        })),
        { value: '__add_new__', label: 'Add New Item' },
      ];
    },
    handleAccountChange() {
      if (String(this.selectedAdjustmentAccount) === '__add_new__') {
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/new-account`);
        this.selectedAdjustmentAccount = '';
      }
    },
    handleWarehouseChange() {
      if (String(this.selectedWarehouse) === '__add_new__') {
        this.showWarehouseModal = true;
        this.selectedWarehouse = '';
      }
    },
    onAddWarehouseOk(warehouse) {
      this.showWarehouseModal = false;
      // Add the new warehouse to the list
      this.warehouses.push(warehouse);
      // Select the newly created warehouse
      this.selectedWarehouse = warehouse.id;
      this.showToastMessage('Warehouse created successfully!', 'success');
    },
    closeWarehouseModal() {
      this.showWarehouseModal = false;
    },
    handleItemChange(row, idx) {
      if (String(row.item) === '__add_new__') {
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/inventory/new-item`);
        this.tableRows[idx].item = '';
        return;
      }
      const selected = this.inventoryItems.find((item) => String(item.id) === String(row.item));
      if (selected) {
        this.tableRows[idx].currentQty = selected.onHandQuantity ?? '';
        this.tableRows[idx].currentValue = selected.salesPrice != null ? Number(selected.salesPrice) : 0;
        this.tableRows[idx].rate =
          selected.salesPrice != null
            ? Number(selected.salesPrice)
            : (selected.purchasePrice != null ? Number(selected.purchasePrice) : null);
      }
    },
    addRow() {
      this.tableRows.push({ id: this.nextRowId++, item: '', comment: '', currentQty: '', adjustedQty: '', currentValue: '0.00', rate: '' });
    },
    removeRow(idx) {
      this.tableRows.splice(idx, 1);
    },
    onAdjustedQtyChange(row, value) {
      row.adjustedQty = value === '' ? '' : Number(value);
      this.validateAdjustedQty(row);
    },
    validateAdjustedQty(row) {
      const current = Number(row.currentQty) || 0;
      const adjusted = Number(row.adjustedQty) || 0;
      // Prevent reducing below zero or exceeding current
      if (adjusted < -current || adjusted > current) {
        row.adjustedQtyError = true;
      } else {
        row.adjustedQtyError = false;
      }
    },
    getAvailableItems(currentRow) {
      const selectedIds = this.tableRows
        .filter((row) => row !== currentRow)
        .map((row) => row.item)
        .filter(Boolean)
        .map(String);
      return this.inventoryItems.filter(
        (item) => !selectedIds.includes(String(item.id)) && !this.adjustedItemIds.includes(item.id)
      );
    },
    async saveStockAdjustment(action) {
      this.saveMessage = '';
      this.saveMessageType = '';
      this.isSaving = true;
      
      // Validate required fields
      if (!this.selectedWarehouse) {
        this.showToastMessage('Please select a warehouse.', 'error');
        this.isSaving = false;
        return;
      }
      
      if (!this.selectedAdjustmentAccount) {
        this.showToastMessage('Please select an adjustment account.', 'error');
        this.isSaving = false;
        return;
      }
      
      // Validate that at least one item is selected
      const validRows = this.tableRows.filter(row => row.item && row.adjustedQty);
      if (validRows.length === 0) {
        this.showToastMessage('Please add at least one item with adjusted quantity.', 'error');
        this.isSaving = false;
        return;
      }
      
      let hasQtyError = false;
      for (const row of this.tableRows) {
        const current = Number(row.currentQty) || 0;
        const adjusted = Number(row.adjustedQty) || 0;
        if (adjusted < -current || adjusted > current) {
          row.adjustedQtyError = true;
          hasQtyError = true;
        } else {
          row.adjustedQtyError = false;
        }
      }
      if (hasQtyError) {
        this.showToastMessage('One or more Adjusted Qty values are invalid.', 'error');
        this.isSaving = false;
        return;
      }
      const companyId = this.$route.params.id;
      const toNullableNumber = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };
      const payload = {
        warehouseId: this.selectedWarehouse,
        adjustmentAccount: this.selectedAdjustmentAccount,
        items: validRows.map(row => ({
          inventoryItemId: row.item,
          comment: row.comment || '',
          currentQty: toNullableNumber(row.currentQty),
          adjustedQty: toNullableNumber(row.adjustedQty),
          currentValue: toNullableNumber(row.currentValue),
          rate: toNullableNumber(row.rate)
        }))
      };
      let hasReorderError = false;
      this.reorderPointErrorRows = [];
      for (const row of this.tableRows) {
        const item = this.inventoryItems.find(i => i.id === row.item);
        if (item && item.reorderPoint != null && item.onHandQuantity != null) {
          const newQty = Number(row.currentQty) + Number(row.adjustedQty);
          if (newQty < item.reorderPoint) {
            hasReorderError = true;
            this.reorderPointErrorRows.push(row.id);
            row.reorderError = true;
          } else {
            row.reorderError = false;
          }
        }
      }
      if (hasReorderError) {
        this.showToastMessage('One or more items would fall below their reorder point. Please adjust quantities.', 'error');
        this.isSaving = false;
        return;
      }
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/stock-adjustments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          let serverMessage = 'Failed to save stock adjustment';
          try {
            const errorBody = await response.json();
            if (errorBody?.error) serverMessage = errorBody.error;
          } catch (_) { /* ignore parse errors */ }
          throw new Error(serverMessage);
        }
        if (action === 'close') {
          this.showToastMessage('Stock adjustment saved successfully!', 'success');
          this.$router.push({ name: 'StockAdjustment', params: { id: companyId } });
        } else if (action === 'new') {
          // Reset form for new entry
          this.tableRows = [
            { id: 1, item: '', comment: '', currentQty: '', adjustedQty: '', currentValue: '0.00', rate: '' }
          ];
          this.nextRowId = 2;
          this.selectedWarehouse = '';
          this.selectedAdjustmentAccount = '';
          this.showToastMessage('Stock adjustment saved! You can add another.', 'success');
        }
      } catch (error) {
        this.showToastMessage(error.message || 'Failed to save stock adjustment', 'error');
      } finally {
        this.isSaving = false;
      }
    },
    showToastMessage(msg, type) {
      this.toastMessage = msg;
      this.toastType = type;
      this.showToast = true;
      setTimeout(() => { this.showToast = false; }, 2500);
    },
    cancel() {
      this.$router.back();
    },
    async saveAndClose() {
      await this.saveStockAdjustment('close');
    },
    async saveAndNew() {
      await this.saveStockAdjustment('new');
    },
  }
};
</script> 